/**
 * Undo/redo — room designer: snapshot(); editor: change()/open().
 */
import type { Engine } from "../engine/Engine";
import type { Engine2 } from "../engine2/Engine2";
import {
  applyChangeEntry,
  buildChangeEntry,
  ChangeEntry,
  ChangeOptions,
  DocumentEntry,
  HistoryDir,
  mergeChangeInto,
  openTransaction,
  StackEntry,
  tryCoalesceChange,
} from "./HistoryObject";
import {
  applyDocument,
  captureDocument,
  cloneDocument,
  documentsEqual,
} from "./documentSnapshot";
import "./snapshots";
import type { SugarObject } from "./SugarObject";

export type { ChangeOptions, OpenTransaction } from "./HistoryObject";

export interface HistoryHost {
  dispatchCustomEvent(event: string, object?: { detail?: unknown }): void;
  activeScene?: unknown;
  getSystem?(key: string): unknown;
}

const DEFAULT_MAX_ENTRIES = 20;

export interface HistoryOptions {
  /** Yığında tutulan en fazla kayıt (varsayılan 20). */
  maxEntries?: number;
}

export class History {
  static readonly KEY = "history";

  private _entries: StackEntry[] = [];
  private _index = -1;
  private _applying = false;
  private _recording = false;
  private _liveDocument: ReturnType<typeof captureDocument> = {};
  private readonly _maxEntries: number;
  /** Açık işlem (beginTransaction…commitTransaction) derinliği. */
  private _txnDepth = 0;
  /** Açık işlemde yığına giren ilk kayıt; sonraki değişiklikler buna katılır. */
  private _txnEntry: ChangeEntry | null = null;

  constructor(
    private readonly host: HistoryHost,
    opts: HistoryOptions = {},
  ) {
    this._maxEntries = opts.maxEntries ?? DEFAULT_MAX_ENTRIES;
  }

  static of(host: Engine | Engine2 | HistoryHost): History | undefined {
    if (host && typeof (host as Engine2).getHistory === "function") {
      return (host as Engine2).getHistory();
    }
    if (host && typeof (host as Engine).getSystem === "function") {
      return (host as Engine).getSystem(History.KEY);
    }
    return undefined;
  }

  static change(
    host: Engine | Engine2 | HistoryHost,
    label: string,
    subjects: unknown[],
    mutate: () => void,
    opts?: ChangeOptions,
  ): void {
    const h = History.of(host);
    if (h) h.change(label, subjects, mutate, opts);
    else mutate();
  }

  get applying(): boolean {
    return this._applying;
  }

  open(subjects: unknown[]) {
    return openTransaction(subjects);
  }

  syncLiveDocument(roots?: SugarObject[]): void {
    if (this._applying) return;
    this._liveDocument = cloneDocument(captureDocument(roots));
  }

  snapshot(label: string, roots?: SugarObject[]): void {
    if (this._applying || this._recording) return;

    const after = captureDocument(roots);
    if (Object.keys(after).length === 0) return;

    const before = cloneDocument(this._liveDocument);
    if (documentsEqual(before, after)) return;

    const entry: DocumentEntry = {
      kind: "document",
      label,
      before,
      after,
      time: performance.now(),
    };
    this._liveDocument = cloneDocument(after);
    this._pushEntry(entry);
  }

  change(
    label: string,
    subjects: unknown[],
    mutate: () => void,
    opts: ChangeOptions = {},
  ): void {
    if (this._applying) {
      mutate();
      return;
    }
    if (this._recording) {
      mutate();
      return;
    }

    this._recording = true;
    try {
      const txn = opts.txn ?? openTransaction(subjects);
      mutate();
      const entry = buildChangeEntry(label, txn, opts);
      if (entry) this._pushChange(entry);
    } finally {
      this._recording = false;
    }
  }

  /**
   * Bir hareketi (ör. slider sürükleme) tek undo kaydı yapar: commit'e kadar gelen
   * change() kayıtları ilk kayda katılır ve en eski "before" korunur. İç içe çağrılabilir.
   */
  beginTransaction(): void {
    this._txnDepth++;
  }

  commitTransaction(): void {
    if (this._txnDepth === 0) return;
    this._txnDepth--;
    if (this._txnDepth === 0) this._txnEntry = null;
  }

  canUndo(): boolean {
    return this._index >= 0;
  }

  canRedo(): boolean {
    return this._index < this._entries.length - 1;
  }

  /**
   * Açık işlemi kapatır. Geri al/yinele açık bir işlemin ortasında gelirse (ör. sürükleme
   * bırakılmadan Cmd+Z) sonraki değişiklikler geri alınmış kayda eklenmesin, işlem asılı kalmasın.
   */
  private _closeTransaction(): void {
    this._txnDepth = 0;
    this._txnEntry = null;
  }

  undo(n = 1): void {
    this._closeTransaction();
    for (let i = 0; i < n && this.canUndo(); i++) {
      this._apply(this._entries[this._index], "undo");
      this._index--;
    }
    this._afterChange();
  }

  redo(n = 1): void {
    this._closeTransaction();
    for (let i = 0; i < n && this.canRedo(); i++) {
      this._index++;
      this._apply(this._entries[this._index], "redo");
    }
    this._afterChange();
  }

  clear(): void {
    this._entries = [];
    this._index = -1;
    this._liveDocument = {};
    // Yığın boşalınca açık işlem de kapanır; yoksa sonraki değişiklikler hiç bitmeyen işleme katılır.
    this._closeTransaction();
    this._afterChange();
  }

  get index(): number {
    return this._index;
  }

  getEntries(): { label: string }[] {
    return this._entries.map((e) => ({ label: e.label }));
  }

  goto(target: number): void {
    this._closeTransaction();
    const clamped = Math.max(-1, Math.min(target, this._entries.length - 1));
    while (this._index > clamped) {
      this._apply(this._entries[this._index], "undo");
      this._index--;
    }
    while (this._index < clamped) {
      this._index++;
      this._apply(this._entries[this._index], "redo");
    }
    this._afterChange();
  }

  private _apply(entry: StackEntry, dir: HistoryDir): void {
    this._applying = true;
    try {
      if (entry.kind === "document") {
        const doc = dir === "undo" ? entry.before : entry.after;
        applyDocument(doc, dir);
        this._liveDocument = cloneDocument(doc);
        this.host.dispatchCustomEvent("scene-mutated", {
          detail: this.host.activeScene,
        });
        return;
      }
      if (entry.kind === "scope") {
        const entries = entry.entries;
        if (dir === "undo") {
          for (let i = entries.length - 1; i >= 0; i--) {
            this._applyChange(entries[i], dir);
          }
        } else {
          for (const e of entries) this._applyChange(e, dir);
        }
        entry.effect?.(dir);
        return;
      }
      this._applyChange(entry, dir);
    } finally {
      this._applying = false;
    }
  }

  private _applyChange(entry: ChangeEntry, dir: HistoryDir): void {
    applyChangeEntry(entry, dir, this.host);
  }

  private _pushChange(entry: ChangeEntry): void {
    const top = this._entries[this._index];
    if (this._txnDepth > 0) {
      // İşlem açıkken ilk kayıt yığının tepesinde duruyorsa ona katılır.
      if (this._txnEntry && top === this._txnEntry) {
        mergeChangeInto(this._txnEntry, entry);
        this._afterChange();
        return;
      }
      this._txnEntry = entry;
      this._pushEntry(entry);
      return;
    }
    if (
      top?.kind === "change" &&
      this._index === this._entries.length - 1 &&
      tryCoalesceChange(top, entry)
    ) {
      this._afterChange();
      return;
    }
    this._pushEntry(entry);
  }

  private _pushEntry(entry: StackEntry): void {
    if (this._index < this._entries.length - 1) {
      this._entries.splice(this._index + 1);
    }
    this._entries.push(entry);
    while (this._entries.length > this._maxEntries) {
      this._entries.shift();
    }
    this._index = this._entries.length - 1;
    this._afterChange();
  }

  private _afterChange(): void {
    this.host.dispatchCustomEvent("editor-history-changed", {
      detail: { canUndo: this.canUndo(), canRedo: this.canRedo() },
    });
  }
}
